import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Check, Users, Hotel, Heart, Shield, MapPin, Calendar, Sun, Mountain, Plane, User, Send } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const dayByDay = [
  { day: "Day 1", title: "Arrival in Delhi", description: "Evening or night arrival in Delhi. You're met at the airport and driven by private car to your 5-star hotel. Check in, exhale, and rest." },
  { day: "Day 2", title: "Delhi: First Taste of India", description: "Easy morning. Then a guided introduction to Old and New Delhi, mixing must-see sights with quieter corners so you can ease into the city at your own pace." },
  { day: "Day 3", title: "Delhi to Agra", description: "Drive to Agra in a comfortable vehicle. Visit the Agra Fort and enjoy a first sunset view towards the Taj Mahal from a beautiful vantage point." },
  { day: "Day 4", title: "Taj Mahal at Sunrise, then Jaipur", description: "Early start for a sunrise visit to the Taj Mahal—cooler, softer light, and fewer crowds. After breakfast, continue on to Jaipur, your base for Rajasthan's palaces and forts." },
  { day: "Day 5", title: "Jaipur: Palaces, Forts & Markets", description: "Explore Jaipur's highlights with a private guide: Amber Fort, City Palace, and hand-picked shops and markets. Return to your palace-style hotel for a relaxed evening." },
  { day: "Day 6", title: "Jaipur at Leisure, Optional Experiences", description: "Enjoy a slower day: pool time, spa, or optional activities such as a cooking experience or a gentle walk through the old city." },
  { day: "Day 7", title: "To the Himalayan Foothills", description: "Travel to a carefully chosen Himalayan foothill town (such as Rishikesh region or similar), where the air is cooler and the pace slows down again." },
  { day: "Day 8", title: "Himalayan Calm", description: "Wake up to mountain views. Light guided walks, time by the river, or simply enjoy the property. This is your chance to absorb the trip so far." },
  { day: "Day 9", title: "Return to Delhi", description: "Head back towards Delhi for your final night, with time for last-minute shopping or a farewell dinner." },
  { day: "Day 10", title: "Departure", description: "Private transfer to the airport for your flight home, with support on timings and logistics." },
];

const faqs = [
  {
    question: "Is this trip suitable if I've never been to Asia before?",
    answer: "Yes. Many guests are visiting Asia for the first time. We plan the itinerary and daily pacing with first-timers in mind and are there to support you at every step."
  },
  {
    question: "How physically demanding is the trip?",
    answer: "You should be comfortable walking for parts of the day on uneven surfaces and climbing some stairs at forts and monuments, but we keep the pace moderate and build in downtime."
  },
  {
    question: "Are flights included?",
    answer: "International flights to and from India are not included. We'll help you choose good options from your home airport on the US East Coast."
  },
  {
    question: "What kinds of hotels do we stay in?",
    answer: "Primarily 5-star or equivalent properties in each destination, often with a sense of place—heritage or palace-style where possible, and high comfort everywhere."
  },
  {
    question: "Is this trip right for solo travelers or couples?",
    answer: "Yes. Past departures have included solo travelers, couples, and friends traveling together, all within the small group."
  },
  {
    question: "What if I have dietary restrictions or health concerns?",
    answer: "We accommodate dietary needs and can adjust the pace for any physical considerations. Just let us know when you inquire, and we'll make sure the journey works for you."
  }
];

export default function FirstTripPage() {
  const formRef = useRef(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    cityArea: "",
    travelTiming: "",
    attraction: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/landing-leads`, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        city_area: formData.cityArea,
        travel_timing: formData.travelTiming,
        attraction: formData.attraction,
        source: "first-trip-to-india"
      });

      toast.success("Thank you! We'll send you the detailed itinerary shortly.");
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        cityArea: "",
        travelTiming: "",
        attraction: ""
      });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Error submitting form:", error);
      }
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="first-trip-page">
      <SEO 
        title="First Trip to India - 10 Day Small Group Journey"
        description="Taj Mahal, Jaipur's palaces, and the Himalayas – designed for 35-65 year olds visiting India for the first time. Small group luxury tour with 5-star hotels."
        keywords={["first trip to India", "India tour for beginners", "luxury India tour for seniors", "small group India tour", "first time India travel"]}
        url="/first-trip-to-india"
      />

      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center bg-[#243b5a] pt-24">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1564507592333-c60657eea523?w=1920&q=80" 
            alt="Taj Mahal at sunrise"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#243b5a] via-[#243b5a]/80 to-transparent" />
        </div>
        
        <div className="container-max relative z-10 px-6 md:px-12 lg:px-24 py-16">
          <div className="max-w-3xl">
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6">
              First Trip to India<br />
              <span className="text-[#D4AF37]">10-Day Small Group Journey</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/90 font-light mb-6">
              Taj Mahal, Jaipur's palaces, and the Himalayas – designed for 35–65 year olds visiting India for the first time.
            </p>
            <p className="text-white/70 text-lg leading-relaxed mb-8 max-w-2xl">
              If India has been on your list for years—but the idea of planning it feels overwhelming—this journey is for you. This 10-day small group trip combines iconic sights with 5-star comfort, thoughtful pacing, and personal hosting, so your first time in India feels inspiring, not exhausting.
            </p>
            <Button 
              onClick={scrollToForm}
              className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#243b5a] font-semibold text-lg px-8 py-4 rounded-md"
              data-testid="hero-cta-btn"
            >
              Request Itinerary & Pricing Call
            </Button>
          </div>
        </div>
      </section>

      {/* Who This Journey Is For */}
      <section className="py-16 bg-white">
        <div className="container-max">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase font-medium">Perfect For You</span>
              <h2 className="font-serif text-3xl md:text-4xl text-[#243b5a] mt-4">
                Made for First-Time India Travelers, 35–65
              </h2>
            </div>
            
            <div className="bg-[#F9F9F7] rounded-2xl p-8 md:p-12">
              <p className="text-[#4A4A4A] text-lg mb-8">
                This journey is designed for curious, comfort-loving travelers who:
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  "Are roughly 35–65 and want India \"done right\" the first time.",
                  "Prefer small groups and personal attention over big buses and rushed checklists.",
                  "Want 5-star luxury hotels, private guides, and vetted experiences—not backpacking or \"roughing it.\"",
                  "Live in the US (especially the East Coast) and want help choosing the right flights and timings."
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <div className="w-6 h-6 rounded-full bg-[#D4AF37] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-[#4A4A4A] text-lg">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[#243b5a] font-medium text-lg italic">
                If you've been saying "one day, I'll see India" for years, this is the trip that finally makes it easy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Journey Highlights */}
      <section className="py-16 bg-[#F9F9F7]">
        <div className="container-max">
          <div className="text-center mb-12">
            <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase font-medium">Quick Snapshot</span>
            <h2 className="font-serif text-3xl md:text-4xl text-[#243b5a] mt-4">
              What You'll Experience in 10 Days
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { icon: Sun, text: "Taj Mahal at sunrise with a trusted local guide." },
              { icon: MapPin, text: "Old and New Delhi: historic sites, markets, and a gentle introduction to the city's energy." },
              { icon: Hotel, text: "Jaipur's royal palaces, forts, and a palace-style 5-star stay." },
              { icon: Mountain, text: "A stay in the Himalayan foothills for clean air, calm, and reflection." },
              { icon: Heart, text: "5-star or equivalent and palace hotels throughout, with daily breakfast and curated dinners." },
              { icon: Users, text: "Small group: maximum 8 guests per departure, so you're never part of a crowd. Private airport transfers and personalized attention." },
            ].map((item, i) => (
              <div key={i} className="bg-[#F9F9F7] rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                <div className="w-12 h-12 rounded-full bg-[#243b5a]/10 flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-[#243b5a]" />
                </div>
                <p className="text-[#4A4A4A]">{item.text}</p>
              </div>
            ))}
          </div>
          
          <p className="text-center text-[#4A4A4A] italic mt-10 text-lg">
            "You focus on enjoying India. We quietly handle the rest in the background."
          </p>
        </div>
      </section>

      {/* Day by Day Overview */}
      <section className="py-16 bg-white">
        <div className="container-max">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase font-medium">Sample Itinerary</span>
              <h2 className="font-serif text-3xl md:text-4xl text-[#243b5a] mt-4">
                A Gentle, Carefully Paced Itinerary
              </h2>
              <p className="text-[#4A4A4A] mt-4 max-w-2xl mx-auto">
                Here's a high-level look at how your 10 days in India unfold. The exact order may vary slightly by departure date and flight times.
              </p>
            </div>

            <div className="space-y-4">
              {dayByDay.map((day, i) => (
                <div 
                  key={i} 
                  className="bg-[#F9F9F7] rounded-xl p-6 border-l-4 border-[#D4AF37] hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-full bg-[#243b5a] flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-serif text-lg">{day.day.split(" ")[1]}</span>
                    </div>
                    <div>
                      <h3 className="font-serif text-xl text-[#243b5a] mb-2">
                        {day.day} – {day.title}
                      </h3>
                      <p className="text-[#4A4A4A]">{day.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 text-center">
              <p className="text-[#4A4A4A] italic mb-6">
                A detailed itinerary with day-by-day descriptions, hotels, and inclusions is available when you request the itinerary.
              </p>
              <Button 
                onClick={scrollToForm}
                className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#243b5a] font-semibold px-8 py-3 rounded-md"
              >
                Request Full Itinerary
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Travel With Us */}
      <section className="py-16 bg-[#F9F9F7]">
        <div className="container-max">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase font-medium">The Difference</span>
              <h2 className="font-serif text-3xl md:text-4xl text-[#243b5a] mt-4">
                Why This Trip Feels Different
              </h2>
              <p className="text-[#4A4A4A] mt-4">
                Personal hosting, small groups, and a deep understanding of both India and US travelers.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  title: "First-timers in mind",
                  description: "The itinerary is designed to balance \"must-see\" sights with downtime, and to gradually introduce you to India's energy, not throw you in the deep end."
                },
                {
                  title: "Small groups, big attention",
                  description: "With a maximum of 8 guests, you're not just a seat on a bus. There's time for questions, conversations, and little adjustments."
                },
                {
                  title: "5-star comfort and vetted experiences",
                  description: "We choose hotels, guides, and drivers we know and trust, so you don't have to guess which online review to believe."
                },
                {
                  title: "Personally hosted by an Indian-American",
                  description: "Your host has lived in the US for decades and knows exactly what comforts, pacing, and clarity you expect—while also knowing India deeply."
                }
              ].map((item, i) => (
                <div key={i} className="bg-white rounded-xl p-8 shadow-sm">
                  <h3 className="font-serif text-xl text-[#243b5a] mb-4">{item.title}</h3>
                  <p className="text-[#4A4A4A]">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Dates and Pricing */}
      <section className="py-16 bg-[#243b5a]">
        <div className="container-max">
          <div className="max-w-4xl mx-auto text-center">
            <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase font-medium">Plan Ahead</span>
            <h2 className="font-serif text-3xl md:text-4xl text-white mt-4 mb-8">
              2026–2027 Departures & Group Details
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6 mb-10">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <Calendar className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
                <h3 className="font-serif text-xl text-white mb-3">Upcoming Departures</h3>
                <ul className="text-white/80 space-y-2 text-left">
                  <li>• October 2026 (perfect season, clear skies)</li>
                  <li>• February 2027 (pleasant weather, great for first timers)</li>
                </ul>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <Users className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
                <h3 className="font-serif text-xl text-white mb-3">Group Size</h3>
                <p className="text-white/80">
                  Maximum 8 guests per departure. Once a date reaches 8 guests, we close it to keep the experience intimate.
                </p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <Heart className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
                <h3 className="font-serif text-xl text-white mb-3">High Value Experience</h3>
                <p className="text-white/80">
                  Reflects 5-star hotels, private guiding, small group size, and personal hosting.
                </p>
              </div>
            </div>
            
            <p className="text-white/70 italic">
              "We're happy to discuss whether this trip is the right fit before you commit."
            </p>
          </div>
        </div>
      </section>

      {/* Lead Form */}
      <section ref={formRef} className="py-16 bg-white" id="request-itinerary">
        <div className="container-max">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-10">
              <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase font-medium">Get Started</span>
              <h2 className="font-serif text-3xl md:text-4xl text-[#243b5a] mt-4">
                Request the Detailed Itinerary & Quick Call
              </h2>
              <p className="text-[#4A4A4A] mt-4">
                Share a few details below and we'll send you the full day-by-day itinerary and set up a short, no-pressure call to answer questions and discuss dates.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="bg-[#F9F9F7] rounded-2xl p-8 md:p-10" data-testid="landing-lead-form">
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <Label htmlFor="firstName" className="text-[#243b5a] font-medium mb-2 block">First Name *</Label>
                  <Input
                    id="firstName"
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="bg-white border-gray-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                    data-testid="first-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="lastName" className="text-[#243b5a] font-medium mb-2 block">Last Name *</Label>
                  <Input
                    id="lastName"
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="bg-white border-gray-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                    data-testid="last-name-input"
                  />
                </div>
              </div>

              <div className="mb-6">
                <Label htmlFor="email" className="text-[#243b5a] font-medium mb-2 block">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="bg-white border-gray-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                  data-testid="email-input"
                />
              </div>

              <div className="mb-6">
                <Label htmlFor="cityArea" className="text-[#243b5a] font-medium mb-2 block">City / Metro Area *</Label>
                <Input
                  id="cityArea"
                  type="text"
                  required
                  placeholder="e.g., New York, Boston, Philadelphia"
                  value={formData.cityArea}
                  onChange={(e) => setFormData({ ...formData, cityArea: e.target.value })}
                  className="bg-white border-gray-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                  data-testid="city-input"
                />
              </div>

              <div className="mb-6">
                <Label htmlFor="travelTiming" className="text-[#243b5a] font-medium mb-2 block">When are you considering traveling to India? *</Label>
                <select
                  id="travelTiming"
                  required
                  value={formData.travelTiming}
                  onChange={(e) => setFormData({ ...formData, travelTiming: e.target.value })}
                  className="w-full rounded-md border border-gray-200 bg-white px-4 py-3 text-[#4A4A4A] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37]"
                  data-testid="timing-select"
                >
                  <option value="">Select timing...</option>
                  <option value="October 2026">October 2026</option>
                  <option value="February 2027">February 2027</option>
                  <option value="2027 - Flexible">2027 - Flexible on dates</option>
                  <option value="Not sure yet">Not sure yet</option>
                </select>
              </div>

              <div className="mb-8">
                <Label htmlFor="attraction" className="text-[#243b5a] font-medium mb-2 block">What attracts you most about this trip? (Optional)</Label>
                <Textarea
                  id="attraction"
                  rows={3}
                  value={formData.attraction}
                  onChange={(e) => setFormData({ ...formData, attraction: e.target.value })}
                  className="bg-white border-gray-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                  placeholder="Tell us what excites you about visiting India..."
                  data-testid="attraction-textarea"
                />
              </div>

              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full bg-[#D4AF37] hover:bg-[#B8960C] text-[#243b5a] font-semibold py-4 rounded-md text-lg"
                data-testid="submit-btn"
              >
                {isSubmitting ? (
                  "Sending..."
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Send Me the Itinerary
                  </>
                )}
              </Button>

              <p className="text-sm text-[#888888] text-center mt-4">
                We'll only use your details to share information about this journey and to follow up about your interest. No spam, ever.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-white">
        <div className="container-max">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase font-medium">Questions</span>
              <h2 className="font-serif text-3xl md:text-4xl text-[#243b5a] mt-4">
                Common Questions
              </h2>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, i) => (
                <AccordionItem 
                  key={i} 
                  value={`faq-${i}`}
                  className="bg-[#F9F9F7] rounded-xl border-none px-6"
                >
                  <AccordionTrigger className="text-left font-serif text-lg text-[#243b5a] hover:text-[#D4AF37] py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#4A4A4A] pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="mt-10 text-center">
              <p className="text-[#4A4A4A] mb-6">
                If you're not sure whether this is the right fit, the best next step is to request the itinerary and set up a quick, friendly call.
              </p>
              <Button 
                onClick={scrollToForm}
                className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#243b5a] font-semibold px-8 py-3 rounded-md"
              >
                Request Itinerary & Call
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-[#243b5a]">
        <div className="container-max text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">
            Ready to Make India Happen?
          </h2>
          <p className="text-white/70 mb-8 max-w-xl mx-auto">
            Request the detailed itinerary and let's set up a quick call to see if this journey is right for you.
          </p>
          <Button 
            onClick={scrollToForm}
            className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#243b5a] font-semibold text-lg px-10 py-4 rounded-md"
          >
            Get Started Today
          </Button>
        </div>
      </section>
    </div>
  );
}
